import { useClient } from '@cortex/react-query';
import type { Workflow, WorkflowVersion } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState, type CSSProperties } from 'react';

import { formantActorLabel, useUserLabel } from '../../../hooks/useUserLabel';
import { readManagedLockViolations } from '../../../lib/api-errors';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { EmptyState } from '../../common/EmptyState';
import { Modal } from '../../common/Modal';
import { Spinner } from '../../common/Spinner';
import { useWorkflowEditor } from '../WorkflowEditorContext';
import { ModeToggle, type DiffMode } from '../WorkflowGraphDiff';
import { WorkflowVersionDiffView, resolvePreviousVersionNumber } from '../WorkflowVersionDiffView';

import { BaselinePicker } from './BaselinePicker';

const cardListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
};

const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
};

const cardHeaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
};

const cardMetaRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-3)',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
};

const cardActions: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 'var(--sp-2)',
};

const archivedToggleRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '0 var(--sp-3)',
};

/**
 * Versions list as a stack of cards (rather than a dense table) so the
 * right panel reads well at narrow widths. Each card surfaces version
 * number, status, created-by, both human + relative times, and a single
 * Promote button on rows that aren't already current.
 *
 * Re-uses the same backend contract as the old tabbed view — every
 * version is immutable; Promote only re-points `Workflow.currentVersionId`.
 */
export function VersionsPanel() {
  const { workflow } = useWorkflowEditor();
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate({ from: '/workflows/$workflowId/' });
  // Loosely read the current `?version=` pin so we can badge the row that's
  // being viewed on the canvas. `strict: false` keeps this component usable
  // regardless of which route it's mounted under.
  const search = useSearch({ strict: false }) as { version?: number };
  const viewedVersionNumber = typeof search.version === 'number' ? search.version : null;

  const openOnCanvas = useCallback(
    (versionNumber: number | null) => {
      fork(nav({ search: (prev) => ({ ...prev, version: versionNumber ?? undefined }) }));
    },
    [nav],
  );

  const versions = useQuery({
    queryKey: ['workflow-versions', workflow.id],
    queryFn: () =>
      client.http.get(`v1/workflows/${workflow.id}/versions`).json<WorkflowVersion[]>(),
  });

  // A managed workflow's 409 names the locked parts the version would change.
  const [promoteLockErrors, setPromoteLockErrors] = useState<string[]>([]);
  const promote = useMutation({
    mutationFn: (workflowVersionId: string) =>
      client.http
        .post(`v1/workflows/${workflow.id}/promote-version`, { json: { workflowVersionId } })
        .json<Workflow>(),
    onMutate: () => setPromoteLockErrors([]),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['workflow', workflow.id] }),
        qc.invalidateQueries({ queryKey: ['workflow-versions', workflow.id] }),
      ]);
    },
    onError: async (err) => setPromoteLockErrors(await readManagedLockViolations(err)),
  });

  // On a managed workflow, a version cut before the latest template version
  // carries the previous template's locked parts; the API refuses to promote
  // it, so the button is not offered.
  const templateFloor = useMemo(
    () =>
      workflow.managedBy
        ? Math.max(
            0,
            ...(versions.data ?? [])
              .filter((v) => v.source === 'TEMPLATE')
              .map((v) => v.versionNumber),
          )
        : 0,
    [workflow.managedBy, versions.data],
  );

  // Discard = flip status to ARCHIVED (DEV-16363). Soft cleanup so the
  // version + its runs stay queryable; the panel just hides ARCHIVED rows
  // by default. We re-use the gap-B3 endpoint rather than introducing a
  // new "discard" route — same write, narrower UI label.
  const discard = useMutation({
    mutationFn: (versionId: string) =>
      client.http
        .patch(`v1/workflows/${workflow.id}/versions/${versionId}/status`, {
          json: { status: 'ARCHIVED' },
        })
        .json<WorkflowVersion>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['workflow-versions', workflow.id] });
    },
  });

  const [showArchived, setShowArchived] = useState(false);
  const [diffTarget, setDiffTarget] = useState<WorkflowVersion | null>(null);
  const [diffMode, setDiffMode] = useState<DiffMode>('unified');
  // Which version the diff compares `diffTarget` against. Seeded from
  // `resolvePreviousVersionNumber` when the modal opens, then user-steerable
  // via the picker — comparing a detached candidate against today's mainline
  // (rather than the version it forked from) is a common review need.
  const [diffBaseline, setDiffBaseline] = useState<number | null>(null);

  const items = versions.data ?? [];

  // Reconciled at the use site rather than trusted as stored. `diffBaseline` is
  // a bare number seeded once at click time; the previous derived version
  // re-resolved against `items` on every render and so was immune to `items`
  // changing underneath an open modal (a hard delete, an org switch, a workflow
  // id change). Without this, a dangling number gives a `<select>` with no
  // matching `<option>` — the DOM sets `selectedIndex = -1`, so the control
  // renders blank while React state says otherwise — plus a bare 404 from the
  // diff endpoint with nothing explaining it.
  const diffBaselineIsLive =
    diffBaseline !== null && items.some((v) => v.versionNumber === diffBaseline);
  const effectiveBaseline = diffTarget
    ? diffBaselineIsLive
      ? diffBaseline
      : resolvePreviousVersionNumber(diffTarget, items)
    : null;

  const { visible, archivedCount } = useMemo(() => {
    const archived = items.filter((v) => v.status === 'ARCHIVED');
    return {
      visible: showArchived ? items : items.filter((v) => v.status !== 'ARCHIVED'),
      archivedCount: archived.length,
    };
  }, [items, showArchived]);

  if (versions.isLoading) return <Spinner />;
  if (versions.error)
    return (
      <div className="alert alert--danger" style={{ margin: 'var(--sp-3)' }}>
        Failed to load versions: {(versions.error as Error).message}
      </div>
    );

  if (items.length === 0)
    return (
      <EmptyState
        title="No versions yet"
        description="A version is cut whenever the graph or input/output schema changes."
      />
    );

  return (
    <>
      {archivedCount > 0 ? (
        <div style={archivedToggleRow}>
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={() => setShowArchived((prev) => !prev)}
          >
            {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
          </button>
        </div>
      ) : null}
      <div style={cardListStyle}>
        {visible.map((v) => {
          // Resolved once per card and reused for both the gate and the modal's
          // seed — it's an O(n) scan of every version, so calling it twice per
          // card made rendering the list O(n²).
          const previous = resolvePreviousVersionNumber(v, items);
          return (
            <VersionCard
              key={v.id}
              version={v}
              isCurrent={v.id === workflow.currentVersionId}
              isViewing={v.versionNumber === viewedVersionNumber}
              hasPrevious={previous !== null}
              onView={() => openOnCanvas(v.versionNumber)}
              onViewChanges={() => {
                setDiffMode('unified');
                setDiffBaseline(previous);
                setDiffTarget(v);
              }}
              predatesTemplate={v.versionNumber < templateFloor}
              onPromote={() => promote.mutate(v.id)}
              isPromoting={promote.isPending && promote.variables === v.id}
              promoteDisabled={promote.isPending}
              onDiscard={() => {
                if (
                  window.confirm(
                    `Discard detached v${v.versionNumber}? It will be archived — still queryable but hidden from the list.`,
                  )
                ) {
                  discard.mutate(v.id);
                }
              }}
              isDiscarding={discard.isPending && discard.variables === v.id}
              discardDisabled={discard.isPending}
            />
          );
        })}
      </div>
      {promote.error ? (
        <div className="alert alert--danger" role="alert" style={{ margin: 'var(--sp-3)' }}>
          {promoteLockErrors.length > 0
            ? `This version cannot be promoted: ${promoteLockErrors.join('; ')}`
            : (promote.error as Error).message}
        </div>
      ) : null}
      {discard.error ? (
        <div className="alert alert--danger" style={{ margin: 'var(--sp-3)' }}>
          {(discard.error as Error).message}
        </div>
      ) : null}
      {diffTarget && effectiveBaseline !== null ? (
        <Modal
          open={true}
          onClose={() => {
            setDiffTarget(null);
            // Clear the baseline too, so a stale number can't leak into the next
            // open if the seeding path ever changes.
            setDiffBaseline(null);
          }}
          eyebrow="VERSION DIFF"
          title={`Changes in v${diffTarget.versionNumber}`}
          width="min(820px, 95vw)"
          headerAction={<ModeToggle mode={diffMode} onChange={setDiffMode} />}
          footer={
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setDiffTarget(null)}
            >
              Close
            </button>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            <BaselinePicker
              target={diffTarget}
              baseline={effectiveBaseline}
              options={items}
              onChange={setDiffBaseline}
            />
            <WorkflowVersionDiffView
              workflowId={workflow.id}
              fromVersionNumber={effectiveBaseline}
              toVersionNumber={diffTarget.versionNumber}
              mode={diffMode}
              onModeChange={setDiffMode}
            />
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/**
 * `WorkflowVersion.attribution` is a free-form `Record<string, unknown>` —
 * the API stamps `{ detached: true, basedOnVersionId: <…> }` on rows cut via
 * `POST /v1/workflows/:id/versions`. (DEV-16363.) Read defensively because
 * legacy rows have shapes like `{ patchedNode: …, previousVersionId: … }`.
 */
function isDetachedAttribution(attribution: Record<string, unknown> | undefined | null): boolean {
  return Boolean(attribution && (attribution as { detached?: unknown }).detached === true);
}

/**
 * Who cut or promoted a version, as a name. The platform's own versions
 * (template instantiation and upgrades, auto-refinement) say so; a person
 * is resolved through the org members, then the Formant users API, never
 * shown as an id.
 */
function Actor({ id, source }: { id: string | null; source?: WorkflowVersion['source'] }) {
  const label = useUserLabel(id);
  if (source === 'AUTO_REFINEMENT') return <>Auto-refinement</>;
  if (source === 'TEMPLATE' || !id) return <>{formantActorLabel}</>;
  return <>{label}</>;
}

function VersionCard({
  version: v,
  isCurrent,
  predatesTemplate,
  onPromote,
  isPromoting,
  promoteDisabled,
  onDiscard,
  isDiscarding,
  discardDisabled,
  hasPrevious,
  isViewing,
  onView,
  onViewChanges,
}: {
  version: WorkflowVersion;
  isCurrent: boolean;
  /** Managed workflow only: cut before the latest Formant template version. */
  predatesTemplate: boolean;
  onPromote: () => void;
  isPromoting: boolean;
  promoteDisabled: boolean;
  onDiscard: () => void;
  isDiscarding: boolean;
  discardDisabled: boolean;
  hasPrevious: boolean;
  isViewing: boolean;
  onView: () => void;
  onViewChanges: () => void;
}) {
  const isDetached = isDetachedAttribution(v.attribution);
  const isArchived = v.status === 'ARCHIVED';
  return (
    <article style={cardStyle}>
      <div style={cardHeaderRow}>
        {/* The row id lives in the tooltip; the version number is the name. */}
        <strong className="t-mono" title={`Version id ${v.id}`}>
          v{v.versionNumber}
        </strong>
        {/* One CURRENT; a past mainline version wears no badge at all (every
            one of them is "ACTIVE" in the API, which said nothing). */}
        {isCurrent ? (
          <span
            className="badge badge--success"
            title="Current version — new runs without an explicit workflowVersionId use this one."
          >
            Current
          </span>
        ) : isArchived ? (
          <span
            className="badge badge--neutral"
            title="Archived — hidden from the default list. Still queryable via API; runs that pinned to it still resolve."
          >
            Archived
          </span>
        ) : isDetached ? (
          <span
            className="badge badge--neutral"
            title="Detached / one-off — created off mainline and never was the workflow's current. Trigger explicitly via /v1/runs, or promote to make it current. (DEV-16363.)"
          >
            Detached
          </span>
        ) : null}
        {v.source === 'AUTO_REFINEMENT' ? (
          <span
            className="badge badge--info"
            title="Created by the auto-refinement agent as a candidate change."
          >
            Auto-refinement
          </span>
        ) : v.source === 'IMPORT' ? (
          <span className="badge badge--neutral" title="Created by a workflow-bundle import.">
            Imported
          </span>
        ) : v.source === 'TEMPLATE' ? (
          <span
            className="badge badge--info"
            data-testid="version-source-TEMPLATE"
            title="Instantiated or upgraded from a Formant managed template."
          >
            Formant template
            {typeof v.attribution?.templateVersion === 'number'
              ? ` v${v.attribution.templateVersion}`
              : ''}
          </span>
        ) : null}
      </div>
      <div style={cardMetaRow}>
        <span>
          <span className="t-mono-label">Created</span> {formatDateTime(v.createdAt)} ·{' '}
          {formatRelative(v.createdAt)}
        </span>
        <span>
          <span className="t-mono-label">By</span>{' '}
          <Actor id={v.createdByUserId} source={v.source} />
        </span>
        {v.promotedBy ? (
          <span>
            <span className="t-mono-label">Promoted by</span> <Actor id={v.promotedBy} />
            {v.promotedAt ? <> · {formatRelative(v.promotedAt)}</> : null}
          </span>
        ) : null}
        {v.experimentId ? (
          <span title={`Created from refinement experiment ${v.experimentId}`}>
            <span className="t-mono-label">From</span>{' '}
            <Link
              to="/refinement/overview"
              style={{ color: 'var(--text-link)', textDecoration: 'underline' }}
            >
              suggestion
            </Link>
          </span>
        ) : null}
      </div>
      <div style={cardActions}>
        {!isCurrent ? (
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onView}
            disabled={isViewing}
            title="Load this version's graph onto the canvas (read-only). Promote it to edit."
            style={{ whiteSpace: 'nowrap' }}
          >
            {isViewing ? 'Viewing' : 'View on canvas'}
          </button>
        ) : null}
        {hasPrevious ? (
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onViewChanges}
            title="Show the git-style diff of node configs, edges, and workflow-level schema and dedup settings. Opens comparing against the previous version; you can re-point that in the dialog."
          >
            View changes
          </button>
        ) : null}
        {!isCurrent && isDetached && !isArchived ? (
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onDiscard}
            disabled={discardDisabled}
            title="Archive this one-off version so it stops appearing in the list. Runs that already pinned to it are unaffected. (DEV-16363.)"
          >
            {isDiscarding ? 'Discarding…' : 'Discard'}
          </button>
        ) : null}
        {predatesTemplate ? (
          <span
            className="t-muted"
            data-testid="version-predates-template"
            title="Managed by Formant — this version was cut from an earlier template and cannot become current."
          >
            Earlier template
          </span>
        ) : !isCurrent ? (
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onPromote}
            disabled={promoteDisabled}
            title="Re-point currentVersionId to this version. The graph isn't rewritten — only the FK moves. In-flight runs are unaffected."
          >
            {isPromoting ? 'Promoting…' : 'Promote to current'}
          </button>
        ) : null}
      </div>
    </article>
  );
}
